import { useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Search, CheckCircle, Clock, XCircle, FileText, ArrowLeft } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ApplicationStatus = () => {
  const [referenceNumber, setReferenceNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [application, setApplication] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!referenceNumber.trim()) {
      toast.error('Please enter your reference number');
      return;
    }

    setLoading(true);
    setNotFound(false);
    setApplication(null);

    try {
      const response = await axios.get(`${API}/forms/job-application/status/${referenceNumber.trim()}`);
      setApplication(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        setNotFound(true);
      } else {
        toast.error('Failed to check status. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="w-12 h-12 text-emerald-500" />;
      case 'rejected':
        return <XCircle className="w-12 h-12 text-red-500" />;
      default:
        return <Clock className="w-12 h-12 text-amber-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'rejected':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-amber-100 text-amber-700 border-amber-200';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'approved':
        return 'Approved';
      case 'rejected':
        return 'Not Selected';
      default:
        return 'Under Review';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_egg-inventory-pro/artifacts/vtgmvoj5_Logo%201.jpg" 
            alt="Wilyrash Farm Logo" 
            className="w-16 h-16 rounded-full object-cover mx-auto mb-4 shadow-lg"
          />
          <h1 className="text-2xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Application Status
          </h1>
          <p className="text-slate-500 text-sm">
            Check the status of your job application
          </p>
        </div>

        {/* Search Form */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <form onSubmit={handleSearch}>
            <Label className="text-slate-700">Reference Number</Label>
            <div className="flex gap-2 mt-2">
              <Input 
                value={referenceNumber}
                onChange={(e) => setReferenceNumber(e.target.value.toUpperCase())}
                placeholder="e.g., WF-20260813-A1B2C3"
                className="flex-1 font-mono"
              />
              <Button type="submit" disabled={loading} className="bg-emerald-600 hover:bg-emerald-700">
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Search className="w-5 h-5" />
                )}
              </Button>
            </div>
            <p className="text-xs text-slate-400 mt-2">
              Enter the reference number you received when you submitted your application
            </p>
          </form>
        </div>

        {/* Not Found Message */}
        {notFound && (
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-slate-400" />
            </div>
            <h2 className="text-lg font-semibold text-slate-800 mb-2">Application Not Found</h2>
            <p className="text-slate-500 text-sm mb-4">
              We couldn't find an application with that reference number. Please check and try again.
            </p>
            <p className="text-xs text-slate-400">
              Reference numbers start with "WF-" followed by the date and a code (e.g., WF-20260813-A1B2C3)
            </p>
          </div>
        )}

        {/* Application Status Display */}
        {application && (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            {/* Status Header */}
            <div className={`p-6 text-center border-b ${
              application.status === 'approved' ? 'bg-emerald-50' :
              application.status === 'rejected' ? 'bg-red-50' : 'bg-amber-50'
            }`}>
              {getStatusIcon(application.status)}
              <span className={`inline-block mt-3 px-4 py-1.5 rounded-full text-sm font-semibold border ${getStatusColor(application.status)}`}>
                {getStatusLabel(application.status)}
              </span>
            </div>

            {/* Application Details */}
            <div className="p-6 space-y-4">
              <div className="flex justify-between items-center pb-3 border-b">
                <span className="text-slate-500 text-sm">Reference Number</span>
                <span className="font-mono font-semibold text-slate-800">{application.reference_number}</span>
              </div>
              <div className="flex justify-between items-center pb-3 border-b">
                <span className="text-slate-500 text-sm">Applicant Name</span>
                <span className="font-medium text-slate-800">{application.applicant_name}</span>
              </div>
              <div className="flex justify-between items-center pb-3 border-b">
                <span className="text-slate-500 text-sm">Position Applied</span>
                <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-sm font-medium">
                  {application.position_applied}
                </span>
              </div>
              <div className="flex justify-between items-center pb-3 border-b">
                <span className="text-slate-500 text-sm">Application Date</span>
                <span className="text-slate-800">{formatDate(application.application_date)}</span>
              </div>
              {application.reviewed_at && (
                <div className="flex justify-between items-center pb-3 border-b">
                  <span className="text-slate-500 text-sm">Reviewed On</span>
                  <span className="text-slate-800">{formatDate(application.reviewed_at)}</span>
                </div>
              )}
            </div>

            {/* Interview Details - if scheduled */}
            {application.interview_scheduled && (
              <div className="mx-4 mb-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-5 h-5 text-purple-600" />
                  <h4 className="font-semibold text-purple-800">Interview Scheduled</h4>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-purple-600">Date:</span>
                    <span className="font-medium text-purple-800">{formatDate(application.interview_date)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-purple-600">Time:</span>
                    <span className="font-medium text-purple-800">{application.interview_time}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-purple-600">Location:</span>
                    <span className="font-medium text-purple-800">{application.interview_location}</span>
                  </div>
                </div>
                <p className="text-xs text-purple-600 mt-3">
                  Please arrive 15 minutes early with your ID and certificates.
                </p>
              </div>
            )}

            {/* Status Message */}
            <div className={`p-4 mx-4 mb-4 rounded-lg ${
              application.status === 'approved' ? 'bg-emerald-50 border border-emerald-200' :
              application.status === 'rejected' ? 'bg-red-50 border border-red-200' : 'bg-amber-50 border border-amber-200'
            }`}>
              <p className={`text-sm ${
                application.status === 'approved' ? 'text-emerald-700' :
                application.status === 'rejected' ? 'text-red-700' : 'text-amber-700'
              }`}>
                {application.message}
              </p>
            </div>

            {/* Back Button */}
            <div className="p-4 border-t bg-slate-50">
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => {
                  setApplication(null);
                  setReferenceNumber('');
                }}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Check Another Application
              </Button>
            </div>
          </div>
        )}

        {/* Apply Link */}
        <div className="text-center mt-8">
          <p className="text-slate-500 text-sm mb-2">Haven't applied yet?</p>
          <a 
            href="/apply" 
            className="text-emerald-600 hover:text-emerald-700 font-medium text-sm hover:underline"
          >
            Submit a Job Application →
          </a>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-xs text-slate-400">
          <p>Wilyrash Farm - Poultry Farm Management</p>
        </div>
      </div>
    </div>
  );
};

export default ApplicationStatus;
